/**
 * Typed client for the solex-leads Worker.
 *
 * The panel is a separate deployable from the Worker, so every call here is
 * cross-origin. Two consequences worth knowing before debugging anything:
 *
 *   1. The Worker's ALLOWED_ORIGINS must list this panel's origin. If it does
 *      not, the browser blocks the RESPONSE — the request reaches the Worker
 *      and succeeds there, so the Worker's logs look perfectly healthy while
 *      the panel shows nothing. `ApiError` carries `likelyCors` for exactly
 *      that case so the UI can say so instead of "failed to fetch".
 *   2. There is no cookie and no session. Every request carries its bearer
 *      token explicitly, and the token lives in localStorage.
 *
 * The response shapes below are hand-mirrored from src/api/*.ts in the Worker.
 * They are a duplicated contract across two builds in one repo; changing a
 * field on one side means changing it here.
 */

const RAW_BASE = import.meta.env.VITE_API_BASE ?? "";

/** Trailing slashes make `${base}/admin/...` double up; normalise once. */
export const API_BASE = RAW_BASE.trim().replace(/\/+$/, "");

/** False when the bundle was built without VITE_API_BASE — see wrangler.toml. */
export const isConfigured = API_BASE.length > 0;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** True when the failure looks like a blocked cross-origin response. */
    readonly likelyCors = false,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ---------------------------------------------------------------------------
// Response shapes, mirrored from the Worker
// ---------------------------------------------------------------------------

export type LeadStage = "new" | "talking" | "demo" | "live" | "lost";

export interface PartnerOverview {
  slug: string;
  display_name: string;
  contact_email: string | null;
  contact_telegram: string | null;
  status: string;
  created_at: string;
  visitors: number;
  leads: number;
  live: number;
  last_lead_at: string | null;
}

export interface Lead {
  id: string;
  name: string;
  email: string | null;
  telegram: string | null;
  company: string | null;
  interest: string | null;
  geo: string | null;
  license: string | null;
  ggr: string | null;
  message: string | null;
  ref: string | null;
  country: string | null;
  stage: LeadStage;
  notes: string | null;
  stage_updated_at: string | null;
  created_at: string;
}

export interface AdminOverview {
  partners: PartnerOverview[];
  stages: Record<string, number>;
  unregisteredRefs: { ref: string; count: number }[];
  totalLeads: number;
  stageOrder: LeadStage[];
  config: { telegram: boolean; resend: boolean; visitSalt: boolean; siteOrigin: string };
}

export interface DailyPoint {
  day: string;
  count: number;
}

export interface PartnerCabinet {
  partner: { slug: string; displayName: string; status: string; since: string };
  links: { landing: string; contact: string };
  stats: {
    visitors: number;
    visitorDays: number;
    leads: number;
    conversion: number | null;
    stages: Record<string, number>;
    lastLeadAt: string | null;
  };
  series: { visitors: DailyPoint[]; leads: DailyPoint[] };
  seriesDays: number;
  stageOrder: LeadStage[];
  leads: {
    id: string;
    company: string | null;
    country: string | null;
    stage: LeadStage;
    created_at: string;
  }[];
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

async function request<T>(
  path: string,
  token: string,
  init: RequestInit = {},
): Promise<T> {
  if (!isConfigured) {
    throw new ApiError(0, "This panel was built without VITE_API_BASE set.");
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
  } catch {
    // fetch only rejects for network-level failures, and a CORS block is
    // indistinguishable from an unreachable host at this layer — which is
    // precisely why it deserves to be named as a possibility.
    throw new ApiError(
      0,
      `Could not reach ${API_BASE}. Either the Worker is unreachable, or this panel's origin is missing from its ALLOWED_ORIGINS.`,
      true,
    );
  }

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? `Request failed with HTTP ${res.status}.`);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const adminApi = {
  overview: (token: string) => request<AdminOverview>("/admin/overview", token),

  leads: (token: string, filters: { ref?: string; stage?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.ref) params.set("ref", filters.ref);
    if (filters.stage) params.set("stage", filters.stage);
    const query = params.toString();
    return request<{ leads: Lead[]; total: number }>(
      `/admin/leads${query ? `?${query}` : ""}`,
      token,
    );
  },

  updateLead: (token: string, id: string, patch: { stage?: LeadStage; notes?: string | null }) =>
    request<{ lead: Lead }>(`/admin/leads/${encodeURIComponent(id)}`, token, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  createPartner: (
    token: string,
    input: { slug: string; displayName: string; contactEmail?: string; contactTelegram?: string },
  ) =>
    request<{ partner: PartnerOverview; accessKey: string }>("/admin/partners", token, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  rotateKey: (token: string, slug: string) =>
    request<{ accessKey: string }>(
      `/admin/partners/${encodeURIComponent(slug)}/rotate`,
      token,
      { method: "POST" },
    ),

  setStatus: (token: string, slug: string, status: "active" | "paused") =>
    request<{ partner: PartnerOverview }>(
      `/admin/partners/${encodeURIComponent(slug)}/status`,
      token,
      { method: "POST", body: JSON.stringify({ status }) },
    ),
};

// ---------------------------------------------------------------------------
// Partner cabinet
// ---------------------------------------------------------------------------

export const partnerApi = {
  me: (key: string) => request<PartnerCabinet>("/partner/me", key),
};
